import React, { useEffect, useState } from "react";
import { useAuth } from "../../auth/FirebaseAuth.js";
import { useParams } from "react-router-dom";
import { getUserById } from "../../models/UserFunctions.js"; // Function to fetch user data
import {
  getOrgById,
  getOrgByRef,
  transformGuestsToTable,
  transformUsersToTable,
} from "../../models/OrgFunctions.js"; // Functions to fetch org and client data
import Column from "../../layout/Column.js";
import Row from "../../layout/Row.js";
import Divider from "../../layout/Divider.js";
import Space from "../../layout/Space.js";
import DataGridFull from "../../tables/DataGridFull.js";
import { useTheme } from "@mui/material";
import Icon1 from "../../icons/Icon.js";
import {
  getDocDataById,
  getDocDataByRef,
  getOrgDataById,
  getRefFromId,
  getUserDataById,
  queryCollection,
  updateDocDataField,
} from "../../models/DataFunctions.js";
import TFGPT1 from "../../textfields/TFGPT1.js";
import SecondaryButton from "../../buttons/SecondaryButton.js";
import PrimaryButton from "../../buttons/PrimaryButton.js";
import updateDocDataFieldApi from "../../api/database/updateDocDataFieldApi.js";
import { useAppContext } from "../../config/AppContext.js";
import { getAuth, signInWithCustomToken} from "firebase/auth";
import loginAs from "../../api/loginAs.js";
import { getAuthCentral } from "../../config/FirebaseConfig";
import { getUserClaims, addUserClaim, updateUserClaim, deleteUserClaim } from "../../api/auth/customClaims.js";
import Modal1 from "../../modals/Modal1.js";
import TextField2 from "../../textfields/TextField2.js";
import { useSnackbar } from "notistack";

function Guests({ navigateWorkspace }) {
  const theme = useTheme();
  const { currentUser, loading } = useAuth();

  const [userData, setUserData] = useState(null);
  const [orgData, setOrgData] = useState(null);

  const [searchValue, setSearchValue] = useState("");
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [options, setOptions] = useState([]);

  const [selected_users_list, setSelectedUsersList] = useState([]);

  const [sessions, setSessions] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [userClaims, setUserClaims] = useState(null);
  const [loadingClaims, setLoadingClaims] = useState(false);
  
  // Claims editing state
  const [newClaimKey, setNewClaimKey] = useState("");
  const [newClaimValue, setNewClaimValue] = useState("");
  const [newClaimType, setNewClaimType] = useState("string");
  const [editingClaim, setEditingClaim] = useState(null);
  const [editClaimValue, setEditClaimValue] = useState("");
  
  const { enqueueSnackbar } = useSnackbar();
  const handleClickVariant = (message, variant) => {
    enqueueSnackbar(message, { variant });
  };

  // Function to navigate to a client by ID
  const gotoUser = (id) => {
    navigateWorkspace(`/admin/user/${id}`);
  };

  // Function to navigate to a client by name

  // Function to fetch data for the current user
  const handleFetchData = async () => {
    if (currentUser) {
      // Fetch user data by currentUser's ID
      const user = await getUserById(currentUser.uid);

      // Fetch organization data using the user's org reference
      const org = await getOrgById(user.org_id);

      // Fetch clients associated with the user's organization
      const sessions = await queryCollection("guest_session", []);

      // Transform clients into table format (rows and columns)
      const { rows, columns } = transformGuestsToTable(sessions);

      // Update state if the user data is new or changed
      if (user && user !== userData) {
        setUserData(user);
        setOrgData(org);
        setSessions(sessions);
        setRows(rows);
        setColumns(columns);

        let options_cleaned = sessions.map((user) => user.email);
        options_cleaned = options_cleaned.filter((user) => user !== undefined);

        // Set options for the search bar (client names)
        setOptions(
          options_cleaned.map((session) => ({ value: session, label: session }))
        );
      }
    }
  };
  const { appState, setAppState } = useAppContext();
  const prefix = appState.dev ? appState.dev_url : appState.prod_url;

  const handleRowClick = async (sessionObject) => {
    let sessionId = sessionObject.id;
    const session = sessions.find(s => s.id === sessionId);
    if (!session || !session.uid) return;
    setSelectedSession(session);
    setIsModalOpen(true);
    setLoadingClaims(true);
    setUserClaims(null);

    try {
      const token = await currentUser.getIdToken();
      const claims = await getUserClaims(session.uid, prefix, token);
      setUserClaims(claims);
    } catch (error) {
      console.error("Error fetching user claims:", error);
      setUserClaims({ error: "Failed to load claims" });
    } finally {
      setLoadingClaims(false);
    }
  };

  // Claims management functions
  const fetchUserClaimsForSession = async (userId) => {
    setLoadingClaims(true);
    try {
      const token = await currentUser.getIdToken();
      const claims = await getUserClaims(userId, prefix, token);
      setUserClaims(claims);
    } catch (error) {
      console.error("Error fetching user claims:", error);
      setUserClaims({ error: "Failed to load claims" });
    } finally {
      setLoadingClaims(false);
    }
  };

  const handleAddClaim = async () => {
    if (!newClaimKey.trim() || !newClaimValue.trim()) {
      handleClickVariant("Veuillez remplir la clé et la valeur", "error");
      return;
    }
    if (userClaims.claims && userClaims.claims.hasOwnProperty(newClaimKey)) {
      handleClickVariant("Cette clé existe déjà", "error");
      return;
    }
    
    let processedValue = newClaimValue;
    if (newClaimType === "boolean") {
      processedValue = newClaimValue.toLowerCase() === "true";
    }
    
    try {
      const token = await currentUser.getIdToken();
      await addUserClaim(selectedSession.uid, newClaimKey, processedValue, prefix, token);
      handleClickVariant("Claim ajouté avec succès", "success");
      setNewClaimKey("");
      setNewClaimValue("");
      setNewClaimType("string");
      await fetchUserClaimsForSession(selectedSession.uid);
    } catch (error) {
      console.error("Error adding claim:", error);
      handleClickVariant("Erreur lors de l'ajout du claim", "error");
    }
  };

  const handleEditClaim = (claimKey, currentValue) => {
    setEditingClaim(claimKey);
    setEditClaimValue(String(currentValue));
  };

  const handleSaveEdit = async () => {
    if (!editClaimValue.trim()) {
      handleClickVariant("La valeur ne peut pas être vide", "error");
      return;
    }
    
    try {
      const token = await currentUser.getIdToken();
      let processedValue = editClaimValue;
      
      // Try to preserve the original type
      const originalValue = userClaims.claims[editingClaim];
      if (typeof originalValue === 'boolean') {
        processedValue = editClaimValue.toLowerCase() === 'true';
      }
      
      await updateUserClaim(selectedSession.uid, editingClaim, processedValue, prefix, token);
      handleClickVariant("Claim modifié avec succès", "success");
      setEditingClaim(null);
      setEditClaimValue("");
      await fetchUserClaimsForSession(selectedSession.uid);
    } catch (error) {
      console.error("Error updating claim:", error);
      handleClickVariant("Erreur lors de la modification du claim", "error");
    }
  };

  const handleCancelEdit = () => {
    setEditingClaim(null);
    setEditClaimValue("");
  };

  const handleDeleteClaim = async (claimKey) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le claim "${claimKey}" ?`)) {
      return;
    }
    
    try {
      const token = await currentUser.getIdToken();
      await deleteUserClaim(selectedSession.uid, claimKey, prefix, token);
      handleClickVariant("Claim supprimé avec succès", "success");
      await fetchUserClaimsForSession(selectedSession.uid);
    } catch (error) {
      console.error("Error deleting claim:", error);
      handleClickVariant("Erreur lors de la suppression du claim", "error");
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedSession(null);
    setUserClaims(null);
    // Reset claim editing states
    setNewClaimKey("");
    setNewClaimValue("");
    setNewClaimType("string");
    setEditingClaim(null);
    setEditClaimValue("");
  };

  const handleLoginAs = async (id) => {
    if (true) {
      return;
    }
    const auth = getAuthCentral();

    try {
      if (!id) {
        console.error("Invalid user id");
        return;
      }
      // Sign out the current user before switching
      const token = await currentUser.getIdToken(); // Fetch token from Firebase

      const switch_token = await loginAs(id, token, prefix);

      // Sign in with the custom token
      await signInWithCustomToken(auth, switch_token);

      // Wait until the currentUser is available
      let refreshedUser = null;
      for (let i = 0; i < 10; i++) {
        refreshedUser = auth.currentUser;
        if (refreshedUser) break;
        await new Promise((resolve) => setTimeout(resolve, 200)); // wait 200ms
      }

      if (!refreshedUser) {
        throw new Error("Failed to retrieve the current user after login.");
      }

      // Fetch user data based on currentUser uid
      const userData = await getUserById(refreshedUser.uid);
      if (!userData || !userData.org_id) {
        throw new Error("Invalid user data or missing org_id.");
      }
    } catch (error) {
      console.error("Error during login:", error);
    }
  };

  // Fetch data when the component mounts or when currentUser changes
  useEffect(() => {
    if (currentUser) {
      handleFetchData();
    }
  }, [currentUser]);

  // If the data is still loading or the user is not authenticated, show a loading message
  if (loading || !currentUser || !userData) {
    return <div></div>;
  }

  const handleSelectionModelChange = async (selection) => {
    if (true) {
      return;
    }
    setSelectedUsersList(selection);
    let session = sessions.find((session) => session.id === selection[0]);
    let special = session?.special;
    let type = special?.split("-")[1];
    let id = special?.split("-")[2];

    if (type === "folder") {
      let folder = await getDocDataById("folder", id);
      let master = await getOrgDataById(folder.master_id);
      let user = await getUserDataById(master.users_id[0]);
      await handleLoginAs(user.id);
      navigateWorkspace(`/folder/${id}`);
    }
  };

  // Render the client data
  return (
    <Column align="left" className="h-full w-full pl-4">
      <Row align="left" className="w-full h-full">
        <Column
          align="left"
          className="h-full w-full overflow-hidden"
          y={10}
          sx={{ width: "1200px" }}
        >
          <div className="text-hx font-medium">Sessions particuliers</div>

          <div className="bg-white border-1 border-shadow_25 rounded-full flex flex-row gap-2 items-center px-2">
            <Icon1 icon_name="search" size={20} fill="grey"></Icon1>
            <TFGPT1
              options={options}
              placeholder="Chercher"
              inputValue={searchValue}
              setInputValue={setSearchValue}
              onAction={() => {}}
              className="w-full"
              start_only={false}
            ></TFGPT1>
          </div>

          {/* DataGrid displaying the clients */}

          <div
            style={{ padding: "0px" }}
            className="w-full h-full max-w-c1p2k overflow-y-auto mb-6 pb-6"
          >
            <div className="h-full min-h-6  pb-6">
              <DataGridFull
                rows={rows}
                columns={columns}
                onClick={handleRowClick}
                initial_sort_field="date"
                initial_asc="desc"
                handleSelectionModelChange={handleSelectionModelChange}
              />
            </div>
          </div>
        </Column>

        <Divider orientation="vertical" />
      </Row>

      <Modal1 isOpen={isModalOpen} onClose={closeModal}>
        <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Claims utilisateur</h2>
            <button
              onClick={closeModal}
              className="text-gray-500 hover:text-gray-700"
            >
              <Icon1 icon_name="close" size={24} />
            </button>
          </div>
          
          {selectedSession && (
            <div className="mb-4">
              <p className="text-sm text-gray-600">
                <strong>Email:</strong> {selectedSession.email}
              </p>
              <p className="text-sm text-gray-600">
                <strong>ID de session:</strong> {selectedSession.id}
              </p>
              {selectedSession.uid && (
                <p className="text-sm text-gray-600">
                  <strong>ID utilisateur:</strong> {selectedSession.user_id}
                </p>
              )}
            </div>
          )}

          <Divider />
          <Space height={16} />

          {loadingClaims && (
            <div className="flex justify-center py-8">
              <div className="text-gray-500">Chargement des claims...</div>
            </div>
          )}

          {userClaims && !loadingClaims && (
            <div>
              {userClaims.error ? (
                <div className="text-red-500 py-4">
                  Erreur: {userClaims.error}
                </div>
              ) : (
                <div>
                  <h3 className="font-semibold mb-3">Claims existants:</h3>
                  {userClaims.claims && Object.keys(userClaims.claims).length > 0 ? (
                    <div className="space-y-2 mb-6">
                      {Object.entries(userClaims.claims).map(([key, value]) => (
                        <div key={key} className="border border-gray-200 rounded-md p-3">
                          <div className="flex justify-between items-start">
                            <div className="flex-grow">
                              <div className="font-semibold text-sm text-gray-700">{key}</div>
                              {editingClaim === key ? (
                                <div className="mt-2 flex gap-2 items-center">
                                  <TextField2
                                    value={editClaimValue}
                                    onChange={(e) => setEditClaimValue(e.target.value)}
                                    placeholder="Nouvelle valeur"
                                    className="flex-grow"
                                  />
                                  <SecondaryButton onClick={handleSaveEdit} className="w-fit">
                                    Sauver
                                  </SecondaryButton>
                                  <SecondaryButton onClick={handleCancelEdit} className="w-fit">
                                    Annuler
                                  </SecondaryButton>
                                </div>
                              ) : (
                                <div 
                                  className="mt-1 text-sm text-gray-900 cursor-pointer hover:bg-gray-50 p-1 rounded"
                                  onClick={() => handleEditClaim(key, value)}
                                  title="Cliquer pour modifier"
                                >
                                  {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                </div>
                              )}
                            </div>
                            {editingClaim !== key && (
                              <div className="flex items-center ml-2">
                                <div 
                                  className="cursor-pointer hover:bg-red-50 p-1 rounded"
                                  onClick={() => handleDeleteClaim(key)}
                                  title="Supprimer"
                                >
                                  <Icon1 icon_name="delete" size={16} fill="#ef4444" />
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-gray-500 py-4 mb-6">
                      Aucun claim trouvé pour cet utilisateur.
                    </div>
                  )}

                  <Divider />
                  <Space height={16} />

                  <h3 className="font-semibold mb-3">Ajouter un claim:</h3>
                  <div className="space-y-3">
                    <TextField2
                      label="Clé"
                      value={newClaimKey}
                      onChange={(e) => setNewClaimKey(e.target.value)}
                      placeholder="Ex: role, permission, etc."
                    />
                    <div className="flex flex-col gap-1">
                      <label className="text-sm font-medium">Type de valeur</label>
                      <select 
                        value={newClaimType} 
                        onChange={(e) => setNewClaimType(e.target.value)}
                        className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="string">String</option>
                        <option value="boolean">Boolean</option>
                      </select>
                    </div>
                    <TextField2
                      label="Valeur"
                      value={newClaimValue}
                      onChange={(e) => setNewClaimValue(e.target.value)}
                      placeholder={newClaimType === "boolean" ? "true ou false" : "Ex: admin, moderator, etc."}
                    />
                    <PrimaryButton
                      className="w-fit"
                      onClick={handleAddClaim}
                      disabled={!newClaimKey.trim() || !newClaimValue.trim()}
                    >
                      Ajouter Claim
                    </PrimaryButton>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal1>
    </Column>
  );
}

export default Guests;
